import { useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMusic } from './MusicContext.jsx';
import './MiniPlayer.css';

/* 手画的矢量图标：三角形按视觉重心居中(centroid x=12)，大小适中偏大、不撑满格子。 */
function IcPlay({ s = 32 }) {
  return (
    <svg className="mi" viewBox="0 0 24 24" width={s} height={s} aria-hidden="true">
      <path d="M8 5 L8 19 L20 12 Z" fill="currentColor" />
    </svg>
  );
}
function IcPause({ s = 30 }) {
  return (
    <svg className="mi" viewBox="0 0 24 24" width={s} height={s} aria-hidden="true">
      <rect x="6.6" y="4.5" width="3.8" height="15" rx="1.5" fill="currentColor" />
      <rect x="13.6" y="4.5" width="3.8" height="15" rx="1.5" fill="currentColor" />
    </svg>
  );
}
function IcPrev({ s = 30 }) {
  return (
    <svg className="mi" viewBox="0 0 24 24" width={s} height={s} aria-hidden="true">
      <rect x="4.8" y="5" width="2.8" height="14" rx="1.4" fill="currentColor" />
      <path d="M20.5 5 L20.5 19 L9 12 Z" fill="currentColor" />
    </svg>
  );
}
function IcNext({ s = 30 }) {
  return (
    <svg className="mi" viewBox="0 0 24 24" width={s} height={s} aria-hidden="true">
      <path d="M3.5 5 L3.5 19 L15 12 Z" fill="currentColor" />
      <rect x="16.4" y="5" width="2.8" height="14" rx="1.4" fill="currentColor" />
    </svg>
  );
}

/* 播放模式：顺序播放 = 两个首尾相接的箭头；单曲循环 = 同一个圈里多一个「1」 */
function IcModeList({ s = 24 }) {
  return (
    <svg className="mi" viewBox="0 0 24 24" width={s} height={s} aria-hidden="true"
         fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 11 V9.5 A3 3 0 0 1 7 6.5 H19" />
      <path d="M16 3.5 L19 6.5 L16 9.5" />
      <path d="M20 13 V14.5 A3 3 0 0 1 17 17.5 H5" />
      <path d="M8 20.5 L5 17.5 L8 14.5" />
    </svg>
  );
}
function IcModeOne({ s = 24 }) {
  return (
    <svg className="mi" viewBox="0 0 24 24" width={s} height={s} aria-hidden="true"
         fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 11 V9.5 A3 3 0 0 1 7 6.5 H19" />
      <path d="M16 3.5 L19 6.5 L16 9.5" />
      <path d="M20 13 V14.5 A3 3 0 0 1 17 17.5 H5" />
      <path d="M8 20.5 L5 17.5 L8 14.5" />
      <path d="M11 10.6 L12.4 9.8 V14.4" strokeWidth="1.6" />
    </svg>
  );
}
const MODE_TIP = { list: '顺序播放', one: '单曲循环' };
function IcHeart({ s = 24, on = false }) {
  return (
    <svg className="mi" viewBox="0 0 24 24" width={s} height={s} aria-hidden="true"
         fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
      <path d="M12 20 C7 16.4 3.5 13.3 3.5 9.4 A4.4 4.4 0 0 1 12 7.6 A4.4 4.4 0 0 1 20.5 9.4 C20.5 13.3 17 16.4 12 20 Z" />
    </svg>
  );
}

/* 常驻小播放条：渲染在 .app-shell 内（跟着外壳走、键盘弹起也不乱），坐在导航胶囊上方。 */
export function MiniBar() {
  const { song, playing, curTime, duration, status, toggle, stop, setShowLyrics } = useMusic();
  if (!song) return null;
  const pct = duration ? (curTime / duration) * 100 : 0;
  const cover = song.cover ? song.cover + '?param=200y200' : '';
  return (
    <div className="mp-bar">
      <div className="mp-progress"><div className="mp-progress-fill" style={{ width: pct + '%' }} /></div>
      <div className="mp-in">
        {cover
          ? <img className={'mp-cover' + (playing ? ' spin' : '')} src={cover} alt="" onClick={() => setShowLyrics(true)} />
          : <div className="mp-cover mp-cover-x" onClick={() => setShowLyrics(true)} />}
        <div className="mp-meta" onClick={() => setShowLyrics(true)}>
          <div className="mp-nm">{song.name}</div>
          <div className="mp-ar">{status === 'nourl' ? '这首暂时放不了' : status === 'loading' ? '加载中…' : song.artist}</div>
        </div>
        <button className="mp-btn" onClick={toggle} aria-label={playing ? '暂停' : '播放'}>
          {playing ? <IcPause s={18} /> : <IcPlay s={19} />}
        </button>
        <button className="mp-close" onClick={stop} aria-label="关掉音乐">×</button>
      </div>
    </div>
  );
}

/* 歌词全屏层：渲染在 .app-shell 外（盖过抽屉/导航一切）。 */
export function LyricsOverlay() {
  const {
    song, playing, curTime, duration, toggle, next, prev, seek,
    lyrics, showLyrics, setShowLyrics, requestListen, mode: playMode, setMode: setPlayMode,
    isLiked, toggleLike,
  } = useMusic();
  // 切模式时在按钮上方冒一句「单曲循环」，一会儿就淡掉
  const [modeTip, setModeTip] = useState('');
  const tipTimer = useRef(0);
  const onMode = () => {
    const m = playMode === 'one' ? 'list' : 'one';
    setPlayMode(m);
    setModeTip(MODE_TIP[m]);
    clearTimeout(tipTimer.current);
    tipTimer.current = setTimeout(() => setModeTip(''), 1300);
  };
  useEffect(() => () => clearTimeout(tipTimer.current), []);
  const navigate = useNavigate();

  // 「和爸爸一起听」：把这首歌交给聊天页去发起一轮「一起听」，收起播放器、跳到聊天等他开口。
  const onListenTogether = (e) => {
    e.stopPropagation();
    if (!song) return;
    requestListen(song);
    setShowLyrics(false);
    navigate('/chat');
  };

  // 两种页面：'cover'=大专辑封面 + 当前一两句歌词（默认，像一般音乐 app 打开的样子），
  // 'lyrics'=完整滚动歌词页。点一下中间区域切换（2026-09-03 铃点单）。
  const [mode, setMode] = useState('cover');
  const activeRef = useRef(null);
  const lyIdx = useMemo(() => {
    if (!lyrics.length) return -1;
    let i = lyrics.length - 1;
    for (let k = 0; k < lyrics.length; k++) { if (lyrics[k].t > curTime) { i = k - 1; break; } }
    return i;
  }, [lyrics, curTime]);

  // 每次打开播放器 / 换歌 → 回到封面页
  useEffect(() => { if (showLyrics) setMode('cover'); }, [showLyrics, song?.id]);

  useEffect(() => {
    if (showLyrics && mode === 'lyrics' && activeRef.current) {
      activeRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [lyIdx, showLyrics, mode]);

  if (!showLyrics || !song) return null;
  const liked = isLiked(song.id);
  const cover = song.cover ? song.cover + '?param=500y500' : '';
  const curLine = lyIdx >= 0 ? lyrics[lyIdx] : null;
  const nextLine = lyrics[(lyIdx >= 0 ? lyIdx : -1) + 1] || null;
  const toggleMode = () => setMode((mo) => (mo === 'cover' ? 'lyrics' : 'cover'));

  return (
    <div className="ly-overlay">
      <div className="ly-top">
        {mode === 'lyrics' && (
          <div className="ly-info">
            <div className="ly-nm">{song.name}</div>
            <div className="ly-ar">{song.artist}</div>
          </div>
        )}
        <button className="ly-close" onClick={() => setShowLyrics(false)} aria-label="收起">⌄</button>
      </div>

      {mode === 'cover' ? (
        <div className="ly-hero" onClick={toggleMode} role="button" tabIndex={0} title="点一下看完整歌词">
          {cover
            ? <img className={'ly-hero-art' + (playing ? ' playing' : '')} src={cover} alt="" />
            : <div className="ly-hero-art mp-cover-x" />}
          <div className="ly-hero-nm">{song.name}</div>
          <div className="ly-hero-ar">{song.artist}</div>
          <div className="ly-hero-lyric">
            {lyrics.length === 0 ? (
              <span className="ly-hero-cur dim">静静听吧～</span>
            ) : (
              <>
                <span className="ly-hero-cur">{curLine ? curLine.text : (nextLine ? nextLine.text : '♪')}</span>
                {curLine && curLine.tr && <span className="ly-hero-tr">{curLine.tr}</span>}
                {nextLine && nextLine !== curLine && <span className="ly-hero-next">{nextLine.text}</span>}
              </>
            )}
          </div>
          <button type="button" className="ly-listen" onClick={onListenTogether}>
            <span className="ly-listen-ic" aria-hidden="true">🎧</span> 和爸爸一起听
          </button>
        </div>
      ) : (
        <div className="ly-scroll" onClick={toggleMode} role="button" tabIndex={0} title="点一下回到封面">
          {lyrics.length === 0 ? (
            <div className="ly-none">这首没有歌词，静静听吧～</div>
          ) : (
            lyrics.map((l, i) => (
              <div key={i} ref={i === lyIdx ? activeRef : null} className={'ly-line' + (i === lyIdx ? ' cur' : '')}>
                <div>{l.text}</div>
                {l.tr && <div className="ly-tr">{l.tr}</div>}
              </div>
            ))
          )}
        </div>
      )}

      <div className="ly-ctrl">
        <div className="ly-time">
          <span>{fmt(curTime)}</span>
          <input className="ly-seek" type="range" min="0" max={duration || 0} step="0.1"
                 value={curTime} onChange={(e) => seek(+e.target.value)} />
          <span>{fmt(duration)}</span>
        </div>
        <div className="ly-btns">
          <button className={'ly-mode' + (playMode === 'one' ? ' on' : '')} onClick={onMode} aria-label={MODE_TIP[playMode]}>
            {playMode === 'one' ? <IcModeOne /> : <IcModeList />}
            {modeTip && <span className="ly-mode-tip">{modeTip}</span>}
          </button>
          <button className="ly-side" onClick={prev} aria-label="上一首"><IcPrev /></button>
          <button className="ly-play" onClick={toggle} aria-label={playing ? '暂停' : '播放'}>{playing ? <IcPause /> : <IcPlay />}</button>
          <button className="ly-side" onClick={next} aria-label="下一首"><IcNext /></button>
          <button className={'ly-mode ly-like' + (liked ? ' on' : '')} onClick={() => toggleLike(song)}
                  aria-label={liked ? '取消喜欢' : '喜欢'}>
            <IcHeart on={liked} />
          </button>
        </div>
      </div>
    </div>
  );
}

function fmt(s) {
  s = Math.max(0, Math.floor(s || 0));
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return m + ':' + (ss < 10 ? '0' : '') + ss;
}
